// src/components/admin/AdminSaldos.jsx
import React from "react";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import { ausenciasService } from "../../services/ausencias";

const yearsAround = (n = 2) => {
  const y = new Date().getFullYear();
  return Array.from({ length: n * 2 + 1 }, (_, i) => y - n + i);
};

export default function AdminSaldos({ token }) {
  const [usuarios, setUsuarios] = React.useState([]);
  const [email, setEmail] = React.useState("");
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [loading, setLoading] = React.useState(false);
  const [saldos, setSaldos] = React.useState([]);

  // ==== Carga usuarios (selector) ====
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await api.get("/usuarios", token);
        if (mounted) setUsuarios(Array.isArray(list) ? list : []);
      } catch {
        if (mounted) setUsuarios([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  // ==== Cargar saldos ====
  const cargar = React.useCallback(
    async () => {
      if (!email) return;
      setLoading(true);
      try {
        const r = await ausenciasService.getBalance(token, { email, year });
        setSaldos(Array.isArray(r?.saldos) ? r.saldos : []);
      } catch (e) {
        console.error("❌ getBalance:", e);
        setSaldos([]);
        toast.error(e?.message || "Error al cargar saldos");
      } finally {
        setLoading(false);
      }
    },
    [token, email, year]
  );

  // ==== UI ====
  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div
        className="
          sticky top-20 z-10
          rounded-2xl border border-white/50 bg-white/60 backdrop-blur-md
          shadow-sm px-3 sm:px-4 py-3
        "
      >
        <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4">
          <div className="min-w-[240px]">
            <label className="block text-xs text-slate-600">
              Usuario (email)
            </label>
            <select
              className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-300 bg-white/90 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            >
              <option value="">— Selecciona usuario —</option>
              {usuarios.map((u) => (
                <option key={u.id ?? u.email} value={u.email}>
                  {u.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-slate-600">Año</label>
            <select
              className="mt-1 px-3 py-2 rounded-xl border border-gray-300 bg-white/90 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {yearsAround(2).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={cargar}
              disabled={!email || loading}
              className="px-4 h-10 rounded-xl bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
              title="Cargar saldos"
            >
              {loading ? "Cargando…" : "Cargar"}
            </button>
            <button
              onClick={() => {
                if (email) cargar();
              }}
              disabled={!email || loading}
              className="px-4 h-10 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
              title="Refrescar"
            >
              Refrescar
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="rounded-2xl border border-white/50 bg-white/60 backdrop-blur-md shadow-sm p-4 sm:p-5">
        {!email ? (
          <p className="text-sm text-slate-600">
            Selecciona un usuario para ver sus saldos…
          </p>
        ) : loading ? (
          <p className="text-sm text-slate-600">Cargando…</p>
        ) : saldos.length === 0 ? (
          <p className="text-sm text-slate-600">
            No hay saldos para <b>{email}</b> en <b>{year}</b>.
          </p>
        ) : (
          <>
            {/* Tabla (≥ md) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2">Tipo</th>
                    <th className="px-3 py-2">Cómputo</th>
                    <th className="px-3 py-2">Mediodía</th>
                    <th className="px-3 py-2">D. anuales</th>
                    <th className="px-3 py-2">Asignado</th>
                    <th className="px-3 py-2">Arrastre</th>
                    <th className="px-3 py-2">Gastado</th>
                    <th className="px-3 py-2 font-semibold">Disponible</th>
                  </tr>
                </thead>
                <tbody>
                  {saldos.map((s) => (
                    <tr key={s.tipo} className="border-t">
                      <td className="px-3 py-2 font-medium">{s.tipo}</td>
                      <td className="px-3 py-2">{s.computo}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs ${
                            s.permite_mediodia
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {s.permite_mediodia ? "sí" : "no"}
                        </span>
                      </td>
                      <td className="px-3 py-2">{s.dias_anuales}</td>
                      <td className="px-3 py-2">{s.asignado}</td>
                      <td className="px-3 py-2">{s.arrastre}</td>
                      <td className="px-3 py-2">{s.gastado}</td>
                      <td className="px-3 py-2 font-semibold text-blue-800">
                        {s.disponible}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Tarjetas (móvil) */}
            <div className="md:hidden grid grid-cols-1 gap-3">
              {saldos.map((s) => (
                <div
                  key={s.tipo}
                  className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-blue-900">{s.tipo}</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <Item k="Cómputo" v={s.computo} />
                    <Item
                      k="Mediodía"
                      v={
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs ${
                            s.permite_mediodia
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {s.permite_mediodia ? "sí" : "no"}
                        </span>
                      }
                    />
                    <Item k="D. anuales" v={s.dias_anuales} />
                    <Item k="Asignado" v={s.asignado} />
                    <Item k="Arrastre" v={s.arrastre} />
                    <Item k="Gastado" v={s.gastado} />
                    <div className="col-span-2">
                      <Item
                        k={<span className="font-semibold">Disponible</span>}
                        v={
                          <span className="font-semibold text-blue-800">
                            {s.disponible}
                          </span>
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Item({ k, v }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2">
      <span className="text-gray-600">{k}</span>
      <span className="text-gray-900">{v}</span>
    </div>
  );
}
